import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as XLSX from 'xlsx';
import api, { setBulkOperation } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import Pagination from '../../components/Pagination';
import { showSuccess, showError, showConfirm } from '../../utils/toast';
import { Filter, ChevronDown, ChevronUp, X, Star, Pencil, Trash2, Code, Eye, Sparkles } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';
import { isValidPhone, formatPhoneDisplay } from '../../utils/phoneUtils';

const AdminBusinessesContent = () => {
  const { isCollapsed } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('newest');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBusinessId, setEditingBusinessId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    city: '',
    tags: [],
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    status: 'approved',
    featured: false
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkBusinesses, setBulkBusinesses] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ total: 0, completed: 0, failed: 0, results: [] });
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [bulkMode, setBulkMode] = useState('create');
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showCreateTagModal, setShowCreateTagModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [images, setImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [descriptionViewMode, setDescriptionViewMode] = useState('visual'); // 'visual' | 'html'

  const insertHtmlTag = (tagStart, tagEnd = '') => {
    setFormData(prev => ({
      ...prev,
      description: (prev.description || '') + tagStart + tagEnd
    }));
  };

  const formatDescriptionHtml = () => {
    if (!formData.description) return;
    try {
      let formatted = '';
      let indent = 0;
      const tab = '  ';
      const clean = formData.description.replace(/>\s*</g, '><').trim();
      const tokens = clean.split(/(<\/?[^>]+>)/g).filter(Boolean);
      for (const token of tokens) {
        if (/^<\/[^>]+>/.test(token)) {
          indent = Math.max(0, indent - 1);
          formatted += tab.repeat(indent) + token + '\n';
        } else if (/^<[^>]+[^/>]>/.test(token) && !/^<(area|base|br|col|embed|hr|img|input|link|meta|param|source|track|wbr)/i.test(token)) {
          formatted += tab.repeat(indent) + token + '\n';
          indent++;
        } else {
          const text = token.trim();
          if (text) {
            formatted += tab.repeat(indent) + text + '\n';
          }
        }
      }
      setFormData(prev => ({ ...prev, description: formatted.trim() }));
      showSuccess('HTML format ho gaya!');
    } catch (e) {
      // keep current
    }
  };

  useEffect(() => {
    // Fetch categories and cities on mount for filters
    fetchFilterData();
  }, []);

  useEffect(() => {
    if (statusFilter !== 'all' || categoryFilter !== 'all' || cityFilter !== 'all' || sortFilter !== 'newest') {
      setCurrentPage(1); // Reset to first page when filter changes
    }
  }, [statusFilter, categoryFilter, cityFilter, sortFilter]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when items per page changes
  }, [itemsPerPage]);

  // Open edit modal when navigating from AdminReports with editBusinessId in state
  useEffect(() => {
    const id = location.state?.editBusinessId;
    if (!id) return;
    handleEdit(id);
    navigate('/admin/businesses', { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleEdit and navigate are stable; we only want to react to editBusinessId
  }, [location.state?.editBusinessId]);

  useEffect(() => {
    fetchBusinesses();
    if (showAddModal || showBulkModal || editingBusinessId) {
      fetchModalTags();
    }
  }, [statusFilter, categoryFilter, cityFilter, sortFilter, itemsPerPage, showAddModal, showBulkModal, editingBusinessId, currentPage, searchTerm]);

  const fetchFilterData = async () => {
    try {
      const [catsRes, citiesRes] = await Promise.all([
        api.get('/categories'),
        api.get('/cities'),
      ]);
      setCategories(catsRes.data || []);
      setCities(citiesRes.data || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchModalTags = async () => {
    if (tags.length > 0) return;
    try {
      const tagsRes = await api.get('/tags');
      setTags(tagsRes.data || []);
    } catch (error) {
      console.error('Error fetching modal tags:', error);
    }
  };

  useEffect(() => {
    if (formData.category && formData.category.trim() !== '') {
      const fetchSubcategories = async () => {
        try {
          // Validate that category ID is a valid MongoDB ObjectId format
          const objectIdRegex = /^[0-9a-fA-F]{24}$/;
          if (!objectIdRegex.test(formData.category)) {
            console.warn('Invalid category ID format:', formData.category);
            setSubcategories([]);
            return;
          }

          // Check if category exists in the categories list
          if (categories.length > 0) {
            const categoryExists = categories.some(cat => cat._id.toString() === formData.category);
            if (!categoryExists) {
              console.warn('Category not found in categories list:', formData.category);
              setSubcategories([]);
              return;
            }
          }

          // Only fetch if we have categories loaded or if we're not in edit mode
          if (categories.length === 0 && !editingBusinessId) {
            // Wait for categories to load
            return;
          }

          const response = await api.get(`/subcategories?categoryId=${formData.category}`);
          setSubcategories(response.data || []);
        } catch (error) {
          // Silently handle subcategory fetch errors - don't show toast
          // This prevents "category not found" errors from showing when category is valid but has no subcategories
          if (error.response?.status !== 404) {
            console.error('Error fetching subcategories:', error);
          }
          setSubcategories([]);
        }
      };

      // Only fetch if categories are loaded or we're not editing
      if (categories.length > 0 || !editingBusinessId) {
        fetchSubcategories();
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.category, categories, editingBusinessId]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      const params = {
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(categoryFilter !== 'all' && { category: categoryFilter }),
        city: cityFilter,
        ...(searchTerm && { search: searchTerm }),
        ...(sortFilter === 'newest' && { sort: 'createdAt:desc' }),
        ...(sortFilter === 'oldest' && { sort: 'createdAt:asc' }),
        ...(sortFilter === 'a-z' && { sort: 'name:asc' }),
        ...(sortFilter === 'z-a' && { sort: 'name:desc' }),
        page: currentPage,
        limit: itemsPerPage
      };
      const response = await api.get('/businesses', { params });
      if (response.data && response.data.businesses) {
        setBusinesses(response.data.businesses);
        setPagination(response.data.pagination);
      } else if (Array.isArray(response.data)) {
        // Fallback for non-paginated response
        setBusinesses(response.data);
        setPagination(null);
      } else {
        setBusinesses([]);
        setPagination(null);
      }
    } catch (error) {
      console.error('Error fetching businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.patch(`/businesses/${id}/status`, { status: newStatus });
      setCurrentPage(1); // Reset to first page after status change
      fetchBusinesses();
      showSuccess(`Business status updated to ${newStatus} successfully!`);
    } catch (error) {
      showError(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleToggleFeatured = (business) => {
    if (business.featured) {
      showConfirm(
        'Do you really want to remove this business from favourites? It will no longer appear in the featured section on the home page.',
        async () => {
          try {
            await api.put(`/businesses/${business._id}`, { featured: false });
            fetchBusinesses();
            showSuccess('Removed from favourites!');
          } catch (error) {
            showError(error.response?.data?.message || 'Error updating');
          }
        }
      );
    } else {
      (async () => {
        try {
          await api.put(`/businesses/${business._id}`, { featured: true });
          fetchBusinesses();
          showSuccess('Added to favourites!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error updating');
        }
      })();
    }
  };

  const handleRemoveListing = (id) => {
    showConfirm(
      'Do you really want to remove this listing? The business will be permanently deleted. This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/businesses/${id}`);
          fetchBusinesses();
          showSuccess('Listing removed successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error removing listing');
        }
      }
    );
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTagToggle = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter((id) => id !== tagId));
    } else {
      setSelectedTags([...selectedTags, tagId]);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      showError('Category name is required');
      return;
    }

    setCreatingCategory(true);
    try {
      const response = await api.post('/categories', {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined
      });

      // Refresh categories list
      const catsRes = await api.get('/categories');
      setCategories(catsRes.data);

      // Auto-select the newly created category
      setFormData({ ...formData, category: response.data._id });

      // Close modal and reset form
      setShowCreateCategoryModal(false);
      setNewCategoryName('');
      setNewCategoryDescription('');

      showSuccess('Category created successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error creating category');
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      showError('Tag name is required');
      return;
    }

    setCreatingTag(true);
    try {
      const response = await api.post('/tags', {
        name: newTagName.trim()
      });

      // Refresh tags list
      const tagsRes = await api.get('/tags');
      setTags(tagsRes.data);

      // Auto-select the newly created tag
      if (!selectedTags.includes(response.data._id)) {
        setSelectedTags([...selectedTags, response.data._id]);
      }

      // Close modal and reset form
      setShowCreateTagModal(false);
      setNewTagName('');

      showSuccess('Tag created successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error creating tag');
    } finally {
      setCreatingTag(false);
    }
  };

  const handleEdit = async (businessId) => {
    try {
      // Ensure categories are loaded first
      // if (categories.length === 0) {
      //   await fetchFormData();
      // }

      const response = await api.get(`/businesses/admin/${businessId}`);
      console.log('response', response);

      const business = response.data;

      if (!business) {
        showError('Business not found');
        return;
      }

      // Get category ID - handle both populated and non-populated cases
      let categoryId = '';
      if (business.category) {
        if (typeof business.category === 'object' && business.category._id) {
          categoryId = business.category._id.toString();
        } else if (typeof business.category === 'string') {
          categoryId = business.category;
        }
      }

      // Verify category exists in categories list
      if (categoryId && categories.length > 0) {
        const categoryExists = categories.some(cat => cat._id.toString() === categoryId);
        if (!categoryExists) {
          console.warn('Category not found in categories list:', categoryId);
          // Don't show error, just log - the category might have been deleted
          categoryId = ''; // Reset to empty to avoid subcategory fetch error
        }
      }

      // Get subcategory ID
      let subcategoryId = '';
      if (business.subcategory) {
        if (typeof business.subcategory === 'object' && business.subcategory._id) {
          subcategoryId = business.subcategory._id.toString();
        } else if (typeof business.subcategory === 'string') {
          subcategoryId = business.subcategory;
        }
      }

      // Get city ID
      let cityId = '';
      if (business.city) {
        if (typeof business.city === 'object' && business.city._id) {
          cityId = business.city._id.toString();
        } else if (typeof business.city === 'string') {
          cityId = business.city;
        }
      }

      // Set form data - but delay setting category to prevent immediate subcategory fetch
      setFormData({
        name: business.name || '',
        category: '', // Set empty first
        subcategory: '',
        city: cityId,
        tags: [],
        address: business.address || '',
        phone: business.phone || '',
        email: business.email || '',
        website: business.website || '',
        description: business.description || '',
        status: business.status || 'approved',
        featured: business.featured || false
      });

      // Set selected tags
      if (business.tags && Array.isArray(business.tags)) {
        const tagIds = business.tags.map(tag => {
          if (typeof tag === 'object' && tag._id) {
            return tag._id.toString();
          }
          return tag;
        }).filter(Boolean);
        setSelectedTags(tagIds);
      } else {
        setSelectedTags([]);
      }

      // Populate existing images so they display in edit modal
      if (business.images && Array.isArray(business.images) && business.images.length > 0) {
        const existingImgs = business.images.map((img, idx) => ({
          url: img.url,
          isPrimary: Boolean(img.isPrimary) || (idx === 0 && !business.images.some(i => i.isPrimary)),
          file: null,
          existing: true,
          id: img._id,
          _id: img._id
        }));
        setImages(existingImgs);
      } else {
        setImages([]);
      }
      setDeletedImageIds([]);

      setEditingBusinessId(businessId);
      setShowAddModal(true);

      // Set category after a small delay to allow modal to open first
      // This prevents the subcategory fetch from running before everything is ready
      setTimeout(() => {
        setFormData(prev => ({
          ...prev,
          category: categoryId,
          subcategory: subcategoryId
        }));
      }, 100);

    } catch (error) {
      console.error('Error loading business for edit:', error);
      const errorMessage = error.response?.data?.message || 'Error loading business data';
      // Only show error if it's not a category-related error from subcategory fetch
      if (!errorMessage.toLowerCase().includes('category not found')) {
        showError(errorMessage);
      } else {
        console.warn('Category-related error ignored:', errorMessage);
      }
    }
  };

  const handleImagesChange = (updatedImages) => {
    setImages(updatedImages);
  };

  const handleImageDelete = (deletedImg) => {
    if (deletedImg?.existing && (deletedImg.id || deletedImg._id)) {
      setDeletedImageIds(prev => [...prev, deletedImg.id || deletedImg._id]);
    }
  };

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const submitData = {
        ...formData,
        tags: selectedTags,
      };

      if (editingBusinessId) {
        // Update existing business details
        await api.put(`/businesses/${editingBusinessId}`, submitData);

        // 1. Delete removed existing images
        if (deletedImageIds.length > 0) {
          for (const imgId of deletedImageIds) {
            try {
              await api.delete(`/businesses/${editingBusinessId}/images/${imgId}`);
            } catch (delErr) {
              console.error('Error deleting image:', delErr);
            }
          }
        }

        // 2. Upload any new image files
        const newImages = images.filter(img => img.file);
        let uploadedImages = [];
        if (newImages.length > 0) {
          const imageFormData = new FormData();
          newImages.forEach((img) => {
            imageFormData.append('images', img.file);
          });

          const primaryNewIndex = newImages.findIndex(img => img.isPrimary);
          if (primaryNewIndex !== -1) {
            imageFormData.append('primaryIndex', primaryNewIndex);
          }

          try {
            const uploadRes = await api.post(`/businesses/${editingBusinessId}/images`, imageFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            uploadedImages = uploadRes.data?.images || [];
          } catch (imgError) {
            console.error('Error uploading images:', imgError);
            showError('Business updated but some new images failed to upload');
          }
        }

        // 3. Update primary image
        const primaryImg = images.find(img => img.isPrimary);
        if (primaryImg) {
          if (primaryImg.existing && (primaryImg.id || primaryImg._id)) {
            try {
              await api.patch(`/businesses/${editingBusinessId}/images/${primaryImg.id || primaryImg._id}/primary`);
            } catch (primErr) {
              console.error('Error setting primary image:', primErr);
            }
          } else if (!primaryImg.existing && uploadedImages.length > 0) {
            const primaryNewIndex = newImages.findIndex(img => img.isPrimary);
            const targetUploaded = (primaryNewIndex !== -1 && uploadedImages[primaryNewIndex])
              ? uploadedImages[primaryNewIndex]
              : uploadedImages[0];
            if (targetUploaded && (targetUploaded._id || targetUploaded.id)) {
              try {
                await api.patch(`/businesses/${editingBusinessId}/images/${targetUploaded._id || targetUploaded.id}/primary`);
              } catch (primErr) {
                console.error('Error setting primary image for new upload:', primErr);
              }
            }
          }
        }

        showSuccess('Business updated successfully!');
      } else {
        // Create new business
        const response = await api.post('/businesses', submitData);
        const businessId = response.data._id;

        // Handle images for new business
        if (images.length > 0) {
          const imageFormData = new FormData();
          images.forEach((img) => {
            if (img.file) {
              imageFormData.append('images', img.file);
            }
          });

          try {
            await api.post(`/businesses/${businessId}/images`, imageFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch (imgError) {
            console.error('Error uploading images:', imgError);
            showError('Business created but some images failed to upload');
          }
        }

        showSuccess('Business added successfully!');
      }

      setShowAddModal(false);
      setEditingBusinessId(null);
      setDescriptionViewMode('visual');
      setFormData({
        name: '',
        category: '',
        subcategory: '',
        city: '',
        tags: [],
        address: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        status: 'approved',
        featured: false
      });
      setSelectedTags([]);
      setTagSearchQuery('');
      setImages([]);
      setDeletedImageIds([]);
      fetchBusinesses();
    } catch (error) {
      showError(error.response?.data?.message || (editingBusinessId ? 'Error updating business' : 'Error adding business'));
    } finally {
      setFormLoading(false);
    }
  };

  // Export All Businesses to Excel
  const exportBusinessesToExcel = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/businesses/export');
      const data = res.data || [];
      if (data.length === 0) {
        showError('No businesses found to export');
        return;
      }

      const rows = data.map((b) => ({
        'ID': b.id,
        'Business Name': b.name,
        'City': b.city,
        'Category': b.category,
        'Subcategory': b.subcategory,
        'Phone': b.phone || '',
        'Address': b.address,
        'Email': b.email,
        'Website': b.website,
        'Status': b.status,
        'Featured': b.featured,
        'Tags': b.tags
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Businesses');

      ws['!cols'] = [
        { wch: 26 }, // ID
        { wch: 25 }, // Business Name
        { wch: 15 }, // City
        { wch: 18 }, // Category
        { wch: 18 }, // Subcategory
        { wch: 16 }, // Phone
        { wch: 30 }, // Address
        { wch: 22 }, // Email
        { wch: 22 }, // Website
        { wch: 12 }, // Status
        { wch: 10 }, // Featured
        { wch: 25 }  // Tags
      ];

      const fileName = `Businesses_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      showSuccess(`Exported ${data.length} businesses successfully!`);
    } catch (error) {
      console.error('Error exporting businesses:', error);
      showError('Failed to export businesses: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Download Excel Template
  const downloadExcelTemplate = () => {
    const templateData = [
      {
        'Business Name': 'Example Business Name',
        'Category': 'Electronics',
        'Subcategory': 'Mobile Phones',
        'City': 'Fatehabad',
        'Address': '123 Main Street, Fatehabad',
        'Phone': '9876543210',
        'Email': 'example@email.com',
        'Website': 'https://example.com',
        'Description': 'This is a sample business description',
        'Tags': 'Tag1, Tag2, Tag3',
        'Status': 'approved',
        'Featured': 'false'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Businesses');

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Business Name
      { wch: 15 }, // Category
      { wch: 15 }, // Subcategory
      { wch: 12 }, // City
      { wch: 30 }, // Address
      { wch: 15 }, // Phone
      { wch: 25 }, // Email
      { wch: 25 }, // Website
      { wch: 40 }, // Description
      { wch: 20 }, // Tags
      { wch: 12 }, // Status
      { wch: 10 }  // Featured
    ];
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, 'Business_Template.xlsx');
    showSuccess('Template downloaded successfully!');
  };

  const handleApplyBulkUpdates = async () => {
    if (!pendingUpdates || pendingUpdates.length === 0) return;
    try {
      setBulkLoading(true);
      const res = await api.post('/admin/businesses/bulk-update', { updates: pendingUpdates });
      showSuccess(res.data?.message || `Successfully updated ${pendingUpdates.length} businesses!`);
      setPendingUpdates([]);
      setShowBulkModal(false);
      fetchBusinesses();
    } catch (error) {
      console.error('Error in bulk update:', error);
      showError(error.response?.data?.message || 'Failed to apply bulk updates');
    } finally {
      setBulkLoading(false);
    }
  };

  // Parse Excel file and create businesses or bulk update
  const parseExcelFile = async (file) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            showError('Excel file is empty');
            return;
          }

          // Check if file contains ID column for bulk update
          const firstRow = jsonData[0];
          const hasIdColumn = 'ID' in firstRow || 'id' in firstRow || '_id' in firstRow;

          if (hasIdColumn) {
            const updates = jsonData.map((row) => ({
              id: (row['ID'] || row['id'] || row['_id'] || '').toString().trim(),
              name: (row['Business Name'] || row['business name'] || row['name'] || '').toString().trim(),
              city: (row['City'] || row['city'] || '').toString().trim(),
              phone: row['Phone'] !== undefined ? String(row['Phone']).trim() : undefined,
              email: row['Email'] !== undefined ? String(row['Email']).trim() : undefined,
              website: row['Website'] !== undefined ? String(row['Website']).trim() : undefined,
              address: row['Address'] !== undefined ? String(row['Address']).trim() : undefined,
              status: row['Status'] !== undefined ? String(row['Status']).trim().toLowerCase() : undefined,
              featured: row['Featured'] !== undefined ? String(row['Featured']).trim().toLowerCase() === 'true' : undefined
            })).filter(u => u.id || u.name);

            setPendingUpdates(updates);
            setBulkMode('update');
            setBulkLoading(false);
            showSuccess(`Loaded ${updates.length} businesses from Excel! Click "Apply Updates" to save.`);
            return;
          }

          setBulkMode('create');
          setPendingUpdates([]);
          setBulkLoading(true);
          setBulkProgress({ total: jsonData.length, completed: 0, failed: 0, results: [] });

          const businesses = [];
          let completed = 0;
          let failed = 0;
          const results = [];

          // Fetch latest categories, tags, and cities before processing
          let currentCategories = categories;
          let currentTags = tags;
          let currentCities = cities;
          try {
            const [catsRes, tagsRes, citiesRes] = await Promise.all([
              api.get('/categories'),
              api.get('/tags'),
              api.get('/cities')
            ]);
            currentCategories = catsRes.data;
            currentTags = tagsRes.data;
            currentCities = citiesRes.data;
            setCategories(currentCategories);
            setTags(currentTags);
            setCities(currentCities);
          } catch (error) {
            console.error('Error fetching categories/tags/cities:', error);
          }

          // Process each row
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            try {
              // Get or create category
              let categoryId = '';
              const categoryName = (row['Category'] || row['category'] || '').toString().trim();
              if (categoryName) {
                // Check if category exists
                let existingCategory = currentCategories.find(c =>
                  c.name.toLowerCase() === categoryName.toLowerCase()
                );

                if (!existingCategory) {
                  // Create new category
                  try {
                    const categoryRes = await api.post('/categories', { name: categoryName });
                    existingCategory = categoryRes.data;
                    // Update local categories list
                    currentCategories = [...currentCategories, existingCategory];
                    setCategories(currentCategories);
                    results.push({
                      success: true,
                      name: `Category: ${categoryName}`,
                      message: 'Created successfully'
                    });
                  } catch (error) {
                    results.push({
                      success: false,
                      name: `Category: ${categoryName}`,
                      message: error.response?.data?.message || 'Failed to create'
                    });
                    continue; // Skip this business if category creation fails
                  }
                }
                categoryId = existingCategory._id;
              }

              // Get or create tags
              const tagsString = (row['Tags'] || row['tags'] || '').toString().trim();
              const tagNames = tagsString ? tagsString.split(',').map(t => t.trim()).filter(Boolean) : [];
              const tagIds = [];

              for (const tagName of tagNames) {
                let existingTag = currentTags.find(t =>
                  t.name.toLowerCase() === tagName.toLowerCase()
                );

                if (!existingTag) {
                  // Create new tag
                  try {
                    const tagRes = await api.post('/tags', { name: tagName });
                    existingTag = tagRes.data;
                    // Update local tags list
                    currentTags = [...currentTags, existingTag];
                    setTags(currentTags);
                  } catch (error) {
                    console.error(`Failed to create tag: ${tagName}`, error);
                    // Continue with other tags even if one fails
                  }
                }
                if (existingTag) {
                  tagIds.push(existingTag._id);
                }
              }

              // Get city
              let cityId = '';
              const cityName = (row['City'] || row['city'] || '').toString().trim();
              if (cityName) {
                const matchedCity = currentCities.find(c =>
                  c.name.toLowerCase() === cityName.toLowerCase()
                );
                if (matchedCity) {
                  cityId = matchedCity._id;
                } else {
                  results.push({
                    success: false,
                    name: row['Business Name'] || `Row ${i + 1}`,
                    message: `City "${cityName}" not found. Please create it first.`
                  });
                  failed++;
                  setBulkProgress({ total: jsonData.length, completed, failed, results: [...results] });
                  continue;
                }
              }

              // Get subcategory if provided
              let subcategoryId = '';
              const subcategoryName = (row['Subcategory'] || row['subcategory'] || '').toString().trim();
              if (subcategoryName && categoryId) {
                try {
                  const subcatsRes = await api.get(`/subcategories?categoryId=${categoryId}`);
                  const subcategories = subcatsRes.data || [];
                  const matchedSubcat = subcategories.find(s =>
                    s.name.toLowerCase() === subcategoryName.toLowerCase()
                  );
                  if (matchedSubcat) {
                    subcategoryId = matchedSubcat._id;
                  }
                } catch (error) {
                  console.error('Error fetching subcategories:', error);
                }
              }

              // Build business object
              const business = {
                name: (row['Business Name'] || row['business name'] || row['name'] || '').toString().trim(),
                category: categoryId,
                subcategory: subcategoryId,
                city: cityId,
                address: (row['Address'] || row['address'] || '').toString().trim(),
                phone: (row['Phone'] || row['phone'] || '').toString().trim(),
                email: (row['Email'] || row['email'] || '').toString().trim(),
                website: (row['Website'] || row['website'] || '').toString().trim(),
                description: (row['Description'] || row['description'] || '').toString().trim(),
                tags: tagIds,
                status: (row['Status'] || row['status'] || 'approved').toString().toLowerCase(),
                featured: (row['Featured'] || row['featured'] || 'false').toString().toLowerCase() === 'true'
              };

              // Validate required fields
              if (!business.name || !business.category || !business.city || !business.phone || !business.address) {
                results.push({
                  success: false,
                  name: business.name || `Row ${i + 1}`,
                  message: 'Missing required fields (name, category, city, phone, address)'
                });
                failed++;
                setBulkProgress({ total: jsonData.length, completed, failed, results: [...results] });
                continue;
              }

              businesses.push(business);
            } catch (error) {
              results.push({
                success: false,
                name: row['Business Name'] || `Row ${i + 1}`,
                message: error.message || 'Error processing row'
              });
              failed++;
            }
          }

          setBulkBusinesses(businesses);
          setBulkProgress({ total: jsonData.length, completed, failed, results });
          setBulkLoading(false);

          if (businesses.length > 0) {
            showSuccess(`Loaded ${businesses.length} business${businesses.length !== 1 ? 'es' : ''} from Excel file${failed > 0 ? ` (${failed} failed)` : ''}`);
          } else {
            showError('No valid businesses found in Excel file');
          }
        } catch (error) {
          setBulkLoading(false);
          showError('Error parsing Excel file: ' + error.message);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      showError('Error reading file: ' + error.message);
    }
  };

  const handleBulkSubmit = async () => {
    console.log('=== BULK SUBMIT STARTED ===');
    console.log('Admin token exists:', !!localStorage.getItem('adminToken'));

    // Check if admin is still logged in before starting
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      showError('You are not logged in. Please login again.');
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 1500);
      return;
    }

    // CRITICAL: Set bulk operation flag IMMEDIATELY to prevent any redirects
    console.log('Setting bulk operation flag to TRUE');
    setBulkOperation(true);

    // Validate all businesses
    const validBusinesses = bulkBusinesses.filter(b =>
      b.name && b.category && b.city && b.phone && b.address
    );

    if (validBusinesses.length === 0) {
      showError('Please fill in at least one complete business (name, category, city, phone, address)');
      return;
    }

    if (validBusinesses.length !== bulkBusinesses.length) {
      const incompleteCount = bulkBusinesses.length - validBusinesses.length;
      showError(`${incompleteCount} business${incompleteCount !== 1 ? 'es' : ''} ${incompleteCount !== 1 ? 'are' : 'is'} incomplete and will be skipped`);
    }

    setBulkLoading(true);
    setBulkProgress({ total: validBusinesses.length, completed: 0, failed: 0, results: [] });

    console.log('Starting bulk submission for', validBusinesses.length, 'businesses');

    const results = [];
    let completed = 0;
    let failed = 0;
    let authErrorOccurred = false;

    try {
      // Process businesses one by one with a small delay to avoid overwhelming the server
      for (let i = 0; i < validBusinesses.length; i++) {
        const business = validBusinesses[i];
        try {
          const submitData = {
            name: business.name,
            category: business.category,
            subcategory: business.subcategory || '',
            city: business.city,
            address: business.address,
            phone: business.phone,
            email: business.email || '',
            website: business.website || '',
            description: business.description || '',
            tags: business.tags || [],
            status: business.status || 'approved',
            featured: business.featured || false
          };

          await api.post('/businesses', submitData);
          completed++;
          results.push({ success: true, name: business.name, message: 'Added successfully' });
        } catch (error) {
          failed++;

          // Check if it's an authentication error
          if (error.response?.status === 401) {
            authErrorOccurred = true;
            const errorMessage = error.response?.data?.message || 'Authentication failed';
            console.error('Authentication error during bulk submit:', {
              businessName: business.name,
              errorMessage,
              status: error.response?.status,
              tokenExists: !!localStorage.getItem('adminToken')
            });

            results.push({
              success: false,
              name: business.name,
              message: errorMessage
            });

            // If we get auth error, stop processing and break the loop
            // Don't continue trying with invalid token
            break;
          } else {
            results.push({
              success: false,
              name: business.name,
              message: error.response?.data?.message || 'Failed to add'
            });
          }
        }

        setBulkProgress({ total: validBusinesses.length, completed, failed, results: [...results] });

        // Small delay between requests
        if (i < validBusinesses.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    } finally {
      // Reset bulk operation flag AFTER all processing is done
      setBulkOperation(false);
      setBulkLoading(false);
    }

    // Handle authentication error after bulk operation completes
    if (authErrorOccurred) {
      // Clear tokens and redirect to login
      localStorage.removeItem('adminToken');
      localStorage.removeItem('userToken');
      localStorage.removeItem('userInfo');

      // Show detailed error message
      const authErrorCount = results.filter(r => r.message.includes('Authentication') || r.message.includes('expired') || r.message.includes('login')).length;
      showError(`Authentication failed after ${completed} successful additions. Your session has expired. Please login again.`);

      // Wait a bit before redirecting to let user see the message
      setTimeout(() => {
        window.location.href = '/admin/login';
      }, 3000);
      return;
    }

    if (completed > 0) {
      showSuccess(`Successfully added ${completed} business${completed !== 1 ? 'es' : ''}`);
      fetchBusinesses();
    }

    // Close modal automatically only if ALL businesses were successfully added
    if (completed === validBusinesses.length && !authErrorOccurred) {
      setShowBulkModal(false);
      setBulkBusinesses([]);
      setBulkProgress({ total: 0, completed: 0, failed: 0, results: [] });
    } else if (failed > 0 && !authErrorOccurred) {
      // If there are any failures, keep modal open to show errors
      showError(`Failed to add ${failed} business${failed !== 1 ? 'es' : ''}. Please check the errors and try again.`);
    }
  };

  return (
    <>
      <SEO title="Manage Businesses - Admin" description="Manage businesses." noindex />

      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Businesses</h1>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search businesses..."
                  className="input-field text-sm flex-1 sm:flex-initial min-w-[150px] sm:min-w-[200px]"
                />
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${showFilters || statusFilter !== 'all' || categoryFilter !== 'all' || cityFilter !== 'all' || sortFilter !== 'newest' || itemsPerPage !== 20
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="hidden sm:inline">Filters</span>
                  {showFilters ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  {(statusFilter !== 'all' || categoryFilter !== 'all' || cityFilter !== 'all' || sortFilter !== 'newest' || itemsPerPage !== 20) && (
                    <span className="bg-white/20 dark:bg-gray-900/30 px-1.5 py-0.5 rounded text-xs font-semibold">
                      {[statusFilter !== 'all' ? 1 : 0, categoryFilter !== 'all' ? 1 : 0, cityFilter !== 'all' ? 1 : 0, sortFilter !== 'newest' ? 1 : 0, itemsPerPage !== 20 ? 1 : 0].reduce((a, b) => a + b, 0)}
                    </span>
                  )}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingBusinessId(null);
                      setImages([]);
                      setDeletedImageIds([]);
                      setDescriptionViewMode('visual');
                      setFormData({
                        name: '',
                        category: '',
                        subcategory: '',
                        city: '',
                        tags: [],
                        address: '',
                        phone: '',
                        email: '',
                        website: '',
                        description: '',
                        status: 'approved',
                        featured: false
                      });
                      setSelectedTags([]);
                      setTagSearchQuery('');
                      setShowAddModal(true);
                    }}
                    className="btn-primary text-sm whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Add Business</span>
                    <span className="sm:hidden">+ Add</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowBulkModal(true);
                      setBulkMode('create');
                      setPendingUpdates([]);
                      setBulkBusinesses([{ name: '', category: '', subcategory: '', city: '', address: '', phone: '', email: '', website: '', description: '', tags: [], status: 'approved', featured: false }]);
                    }}
                    className="btn-secondary text-sm whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Bulk Add / Update</span>
                    <span className="sm:hidden">Bulk</span>
                  </button>
                  <button
                    type="button"
                    onClick={exportBusinessesToExcel}
                    className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap shadow-sm hover:shadow"
                    title="Export all 372 businesses with their phone numbers to Excel"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">Export Excel</span>
                    <span className="sm:hidden">Export</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Collapsible Filters Section */}
            {showFilters && (
              <div className="card p-4 md:p-6 mb-4 animate-slide-down">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2">
                    <Filter className="w-5 h-5" />
                    Filters
                  </h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    aria-label="Close filters"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Status
                    </label>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="input-field text-sm w-full"
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => {
                        setCategoryFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="input-field text-sm w-full"
                    >
                      <option value="all">All Categories</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      City
                    </label>
                    <select
                      value={cityFilter}
                      onChange={(e) => {
                        setCityFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="input-field text-sm w-full"
                    >
                      <option value="all">All Cities</option>
                      {cities.map((city) => (
                        <option key={city._id} value={city._id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sort by Name
                    </label>
                    <select
                      value={sortFilter}
                      onChange={(e) => {
                        setSortFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="input-field text-sm w-full"
                    >
                      <option value="newest">Newest first</option>
                      <option value="a-z">A to Z</option>
                      <option value="z-a">Z to A</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Items Per Page
                    </label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                      }}
                      className="input-field text-sm w-full"
                    >
                      <option value="10">10 per page</option>
                      <option value="20">20 per page</option>
                      <option value="50">50 per page</option>
                      <option value="100">100 per page</option>
                    </select>
                  </div>
                </div>
                {(statusFilter !== 'all' || categoryFilter !== 'all' || cityFilter !== 'all' || sortFilter !== 'newest' || itemsPerPage !== 20) && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setCategoryFilter('all');
                        setCityFilter('all');
                        setSortFilter('newest');
                        setItemsPerPage(20);
                        setCurrentPage(1);
                      }}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <PageLoader message="Loading businesses..." />
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">City</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {businesses.map((business, index) => {
                        const srNo = (currentPage - 1) * itemsPerPage + index + 1;
                        return (
                          <tr key={business._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 dark:text-gray-50">{business.name}</span>
                                {business.featured && (
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400" title="Featured">
                                    <Star className="w-3.5 h-3.5 fill-current" />
                                    Featured
                                  </span>
                                )}
                              </div>
                              <div className="mt-1">
                                {isValidPhone(business.phone) ? (
                                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">{formatPhoneDisplay(business.phone)}</span>
                                ) : (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/50">
                                    No Phone
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 md:px-6 py-4 text-gray-700 dark:text-gray-300">{business.category?.name || 'N/A'}</td>
                            <td className="px-4 md:px-6 py-4 text-gray-700 dark:text-gray-300">{business.city?.name || 'N/A'}</td>
                            <td className="px-4 md:px-6 py-4">
                              <span
                                className={`badge text-xs ${business.status === 'approved'
                                  ? 'badge-success'
                                  : business.status === 'pending'
                                    ? 'badge-warning'
                                    : 'badge-error'
                                  }`}
                              >
                                {business.status}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => handleToggleFeatured(business)}
                                  className="inline-flex items-center gap-1 text-sm font-medium transition-colors px-2 py-1 rounded hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                  title={business.featured ? 'Remove from favourites' : 'Add to favourites'}
                                >
                                  <Star className={`w-4 h-4 ${business.featured ? 'text-amber-500 fill-amber-500' : 'text-gray-400 dark:text-gray-500'}`} />
                                  <span className={business.featured ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}>{business.featured ? 'Remove from Favourites' : 'Add to Favourites'}</span>
                                </button>
                                <button
                                  onClick={() => handleEdit(business._id)}
                                  className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors px-2 py-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20"
                                  title="Edit business"
                                >
                                  <Pencil className="w-4 h-4" />
                                  Edit
                                </button>
                                {business.status !== 'approved' && (
                                  <button
                                    onClick={() => handleStatusChange(business._id, 'approved')}
                                    className="text-success-600 dark:text-success-400 hover:text-success-700 dark:hover:text-success-300 text-sm font-medium transition-colors"
                                  >
                                    Approve
                                  </button>
                                )}
                                {business.status !== 'rejected' && (
                                  <button
                                    onClick={() => handleStatusChange(business._id, 'rejected')}
                                    className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium transition-colors"
                                  >
                                    Reject
                                  </button>
                                )}
                                <button
                                  onClick={() => handleRemoveListing(business._id)}
                                  className="inline-flex items-center gap-1 text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium transition-colors px-2 py-1 rounded hover:bg-error-50 dark:hover:bg-error-900/20"
                                  title="Remove listing (permanent delete)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Remove listing
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {businesses.map((business, index) => {
                  const srNo = (currentPage - 1) * itemsPerPage + index + 1;
                  return (
                    <div key={business._id} className="card p-4 md:p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                            <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{business.name}</h3>
                            {business.featured && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400">
                                <Star className="w-3.5 h-3.5 fill-current" />
                                Featured
                              </span>
                            )}
                          </div>
                          <div className="mb-2">
                            {isValidPhone(business.phone) ? (
                              <span className="text-xs text-green-600 dark:text-green-400 font-medium">{formatPhoneDisplay(business.phone)}</span>
                            ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200/50">
                                No Phone
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mb-3">
                            <span className="badge badge-primary text-xs">{business.category?.name || 'N/A'}</span>
                            <span className="badge text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{business.city?.name || 'N/A'}</span>
                            <span
                              className={`badge text-xs ${business.status === 'approved'
                                ? 'badge-success'
                                : business.status === 'pending'
                                  ? 'badge-warning'
                                  : 'badge-error'
                                }`}
                            >
                              {business.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleToggleFeatured(business)}
                          className="inline-flex items-center gap-1 btn-outline text-sm py-1.5 px-3"
                          title={business.featured ? 'Remove from favourites' : 'Add to favourites'}
                        >
                          <Star className={`w-4 h-4 ${business.featured ? 'text-amber-500 fill-amber-500' : 'text-gray-400'}`} />
                          {business.featured ? 'Remove from Favourites' : 'Add to Favourites'}
                        </button>
                        <button
                          onClick={() => handleEdit(business._id)}
                          className="inline-flex items-center gap-1 btn-outline text-sm py-1.5 px-3 text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400"
                        >
                          <Pencil className="w-4 h-4" />
                          Edit
                        </button>
                        {business.status !== 'approved' && (
                          <button
                            onClick={() => handleStatusChange(business._id, 'approved')}
                            className="btn-outline text-sm py-1.5 px-3"
                          >
                            Approve
                          </button>
                        )}
                        {business.status !== 'rejected' && (
                          <button
                            onClick={() => handleStatusChange(business._id, 'rejected')}
                            className="btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                          >
                            Reject
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveListing(business._id)}
                          className="inline-flex items-center gap-1 btn-outline text-sm py-1.5 px-3 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                        >
                          <Trash2 className="w-4 h-4" />
                          Remove listing
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {businesses.length === 0 && (
                <div className="card p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">No businesses found.</p>
                </div>
              )}
            </>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages >= 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                itemsPerPage={pagination.itemsPerPage}
                itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                hasNextPage={pagination.hasNextPage}
                hasPrevPage={pagination.hasPrevPage}
                nextPage={pagination.nextPage}
                prevPage={pagination.prevPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          )}

          {/* Add Business Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in">
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-4 flex justify-between items-center z-10">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-50">
                    {editingBusinessId ? 'Edit Business' : 'Add New Business'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingBusinessId(null);
                      setDescriptionViewMode('visual');
                      setFormData({
                        name: '',
                        category: '',
                        subcategory: '',
                        city: '',
                        tags: [],
                        address: '',
                        phone: '',
                        email: '',
                        website: '',
                        description: '',
                        status: 'approved',
                        featured: false
                      });
                      setSelectedTags([]);
                      setTagSearchQuery('');
                      setImages([]);
                      setDeletedImageIds([]);
                    }}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl transition-colors"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleAddBusiness} className="p-4 md:p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Business Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Status *</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleFormChange}
                        required
                        className="input-field"
                      >
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div className="flex items-center md:col-span-2">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                      <label className="ml-2 text-gray-700 dark:text-gray-300 font-semibold">⭐ Featured (Show on Home Page)</label>
                    </div>

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Category *</label>
                      <div className="flex gap-2">
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleFormChange}
                          required
                          className="input-field flex-1"
                        >
                          <option value="">Select a category</option>
                          {categories.map((cat) => (
                            <option key={cat._id} value={cat._id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowCreateCategoryModal(true)}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-colors whitespace-nowrap"
                          title="Create New Category"
                        >
                          + New
                        </button>
                      </div>
                    </div>

                    {subcategories.length > 0 && (
                      <div>
                        <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Subcategory</label>
                        <select
                          name="subcategory"
                          value={formData.subcategory}
                          onChange={handleFormChange}
                          className="input-field"
                        >
                          <option value="">Select a subcategory</option>
                          {subcategories.map((subcat) => (
                            <option key={subcat._id} value={subcat._id}>
                              {subcat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">City *</label>
                      <select
                        name="city"
                        value={formData.city}
                        onChange={handleFormChange}
                        required
                        className="input-field"
                      >
                        <option value="">Select a city</option>
                        {cities.map((city) => (
                          <option key={city._id} value={city._id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleFormChange}
                        required
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        className="input-field"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Website</label>
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleFormChange}
                        placeholder="https://example.com"
                        className="input-field"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Address *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleFormChange}
                      required
                      rows="2"
                      className="input-field"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold">Tags</label>
                      <button
                        type="button"
                        onClick={() => setShowCreateTagModal(true)}
                        className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-semibold transition-colors"
                        title="Create New Tag"
                      >
                        + New Tag
                      </button>
                    </div>

                    {/* Tag Search Input */}
                    <div className="relative mb-2">
                      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={tagSearchQuery}
                        onChange={(e) => setTagSearchQuery(e.target.value)}
                        placeholder="Search tags..."
                        className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      {tagSearchQuery && (
                        <button
                          type="button"
                          onClick={() => setTagSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Selected Tags Pills */}
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {selectedTags.map(tagId => {
                          const t = tags.find(tg => tg._id === tagId);
                          if (!t) return null;
                          return (
                            <span
                              key={tagId}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-600 text-white"
                            >
                              {t.name}
                              <button
                                type="button"
                                onClick={() => handleTagToggle(tagId)}
                                className="ml-0.5 hover:text-primary-200 transition"
                              >×</button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Filtered Tags List */}
                    {(() => {
                      const q = tagSearchQuery.toLowerCase().trim();
                      const catName = (categories.find(c => c._id === formData.category)?.name || '').toLowerCase();
                      const bizName = (formData.name || '').toLowerCase();

                      const filtered = tags.filter(t => {
                        if (selectedTags.includes(t._id)) return false;
                        if (q) return t.name.toLowerCase().includes(q);
                        return true;
                      });

                      // Relevance scoring: tags whose name matches category or biz name keyword get higher score
                      const scored = filtered.map(t => {
                        const tn = t.name.toLowerCase();
                        let score = 0;
                        if (catName && (tn.includes(catName) || catName.includes(tn))) score += 3;
                        if (bizName) {
                          bizName.split(' ').forEach(w => {
                            if (w.length > 2 && tn.includes(w)) score += 2;
                          });
                        }
                        if (t.featured) score += 1;
                        return { ...t, score };
                      }).sort((a, b) => b.score - a.score);

                      const suggested = !q ? scored.filter(t => t.score > 0).slice(0, 8) : [];
                      const rest = !q ? scored.filter(t => t.score === 0) : scored;

                      return (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 max-h-52 overflow-y-auto">
                          {suggested.length > 0 && (
                            <>
                              <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/30 border-b border-gray-100 dark:border-gray-800">
                                ✦ Suggested for {categories.find(c => c._id === formData.category)?.name || 'this category'}
                              </div>
                              <div className="flex flex-wrap gap-1.5 p-2.5 border-b border-gray-100 dark:border-gray-800">
                                {suggested.map(tag => (
                                  <button
                                    key={tag._id}
                                    type="button"
                                    onClick={() => handleTagToggle(tag._id)}
                                    className="px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/60 transition"
                                  >
                                    + {tag.name}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                          {rest.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 p-2.5">
                              {rest.map(tag => (
                                <button
                                  key={tag._id}
                                  type="button"
                                  onClick={() => handleTagToggle(tag._id)}
                                  className="px-2.5 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition border border-gray-200 dark:border-gray-700"
                                >
                                  + {tag.name}
                                </button>
                              ))}
                            </div>
                          ) : (
                            suggested.length === 0 && (
                              <div className="p-4 text-center text-sm text-gray-400 dark:text-gray-500">
                                {q ? `No tags matching "${q}"` : 'No tags available'}
                              </div>
                            )
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold">
                        Description
                      </label>
                      <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-700 p-0.5 bg-gray-100 dark:bg-gray-800">
                        <button
                          type="button"
                          onClick={() => setDescriptionViewMode('visual')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            descriptionViewMode === 'visual'
                              ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                          }`}
                          title="WYSIWYG Rich Text Editor"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          Visual Editor
                        </button>
                        <button
                          type="button"
                          onClick={() => setDescriptionViewMode('html')}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                            descriptionViewMode === 'html'
                              ? 'bg-primary-600 text-white shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                          }`}
                          title="Direct HTML Source View & Edit"
                        >
                          <Code className="w-3.5 h-3.5" />
                          HTML View &lt;/&gt;
                        </button>
                      </div>
                    </div>

                    {descriptionViewMode === 'visual' ? (
                      <div className="rich-text-editor">
                        <ReactQuill
                          theme="snow"
                          value={formData.description}
                          onChange={(value) => setFormData({ ...formData, description: value })}
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                              [{ 'font': [] }],
                              [{ 'size': [] }],
                              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                              [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                              [{ 'color': [] }, { 'background': [] }],
                              [{ 'align': [] }],
                              ['link', 'image', 'video'],
                              ['clean']
                            ]
                          }}
                          formats={[
                            'header', 'font', 'size',
                            'bold', 'italic', 'underline', 'strike', 'blockquote',
                            'list', 'bullet', 'indent',
                            'color', 'background',
                            'align',
                            'link', 'image', 'video'
                          ]}
                          placeholder="Enter business description..."
                        />
                      </div>
                    ) : (
                      <div className="space-y-2 border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900">
                        {/* Quick HTML Insertion Chips & Beautifier */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 mr-1">Quick Tags:</span>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<p>', '</p>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Paragraph"
                            >
                              &lt;p&gt;
                            </button>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<h2>', '</h2>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Heading 2"
                            >
                              &lt;h2&gt;
                            </button>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<h3>', '</h3>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Heading 3"
                            >
                              &lt;h3&gt;
                            </button>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<strong>', '</strong>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Bold text"
                            >
                              &lt;strong&gt;
                            </button>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<ul>\n  <li>', '</li>\n</ul>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Unordered list"
                            >
                              &lt;ul&gt;
                            </button>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<a href="https://" target="_blank" rel="noopener noreferrer">', '</a>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Link"
                            >
                              &lt;a&gt;
                            </button>
                            <button
                              type="button"
                              onClick={() => insertHtmlTag('<br/>')}
                              className="px-2 py-0.5 text-xs font-mono bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                              title="Line break"
                            >
                              &lt;br/&gt;
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={formatDescriptionHtml}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/50 border border-primary-200 dark:border-primary-800 rounded hover:bg-primary-100 dark:hover:bg-primary-900/50 transition"
                            title="Indent and format HTML nicely"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            Format HTML
                          </button>
                        </div>

                        {/* Raw HTML Code Editor */}
                        <div className="relative">
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="<!-- Write or paste custom HTML code here -->\n<p>Enter business details in HTML...</p>"
                            rows={11}
                            className="w-full font-mono text-xs md:text-sm p-3.5 rounded-lg border border-gray-700 bg-gray-950 text-emerald-400 dark:bg-gray-950 dark:text-emerald-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition leading-relaxed"
                            spellCheck={false}
                          />
                        </div>

                        <div className="flex flex-wrap items-center justify-between text-xs text-gray-500 dark:text-gray-400 pt-1">
                          <span>💡 आप यहाँ कस्टम HTML टैग्स सीधे एडिट कर सकते हैं। Visual Editor पर स्विच करने पर यह लाइव प्रीव्यू हो जाएगा।</span>
                          <span className="font-mono bg-gray-200 dark:bg-gray-800 px-2 py-0.5 rounded text-[11px]">
                            {formData.description ? formData.description.length : 0} chars
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">
                      Business Images (Optional)
                    </label>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      Add up to 10 images of your business. The first image will be the primary image.
                    </p>
                    <ImageUpload
                      images={images}
                      onImagesChange={handleImagesChange}
                      onImageDelete={handleImageDelete}
                      maxImages={10}
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="submit"
                      disabled={formLoading}
                      className="btn-primary flex-1"
                    >
                      {formLoading
                        ? (editingBusinessId ? 'Updating...' : 'Adding...')
                        : (editingBusinessId ? 'Update Business' : 'Add Business')
                      }
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddModal(false);
                        setEditingBusinessId(null);
                        setDescriptionViewMode('visual');
                        setFormData({
                          name: '',
                          category: '',
                          subcategory: '',
                          city: '',
                          tags: [],
                          address: '',
                          phone: '',
                          email: '',
                          website: '',
                          description: '',
                          status: 'approved',
                          featured: false
                        });
                        setSelectedTags([]);
                        setImages([]);
                        setDeletedImageIds([]);
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Create Category Modal */}
          {showCreateCategoryModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-50">Create New Category</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Category Name *</label>
                    <input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter category name"
                      className="input-field"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !creatingCategory) {
                          handleCreateCategory();
                        }
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Description (Optional)</label>
                    <textarea
                      value={newCategoryDescription}
                      onChange={(e) => setNewCategoryDescription(e.target.value)}
                      placeholder="Enter category description"
                      rows="3"
                      className="input-field"
                    />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCreateCategory}
                      disabled={creatingCategory || !newCategoryName.trim()}
                      className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingCategory ? 'Creating...' : 'Create Category'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateCategoryModal(false);
                        setNewCategoryName('');
                        setNewCategoryDescription('');
                      }}
                      className="btn-secondary flex-1"
                      disabled={creatingCategory}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Tag Modal */}
          {showCreateTagModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md animate-scale-in">
                <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-50">Create New Tag</h2>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Tag Name *</label>
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Enter tag name"
                      className="input-field"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !creatingTag) {
                          handleCreateTag();
                        }
                      }}
                    />
                  </div>
                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleCreateTag}
                      disabled={creatingTag || !newTagName.trim()}
                      className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingTag ? 'Creating...' : 'Create Tag'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateTagModal(false);
                        setNewTagName('');
                      }}
                      className="btn-secondary flex-1"
                      disabled={creatingTag}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Add Business Modal */}
          {showBulkModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto animate-scale-in">
                <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-4 flex justify-between items-center z-10">
                  <div>
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-gray-50">Bulk Add / Update Businesses</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Export existing businesses to fill missing phone numbers, or upload new listings</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkBusinesses([]);
                      setPendingUpdates([]);
                      setBulkMode('create');
                      setBulkProgress({ total: 0, completed: 0, failed: 0, results: [] });
                    }}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl transition-colors"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <div className="p-4 md:p-6">
                  {/* Excel Template Download and Upload */}
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-50 mb-1">
                          Excel Operations (Export & Update)
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Export existing listings to easily fill missing phone numbers, or download a blank template.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={exportBusinessesToExcel}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 whitespace-nowrap shadow-sm"
                          title="Export all existing businesses with ID column to fill phone numbers"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export All Businesses (To Edit Phones)
                        </button>
                        <button
                          type="button"
                          onClick={downloadExcelTemplate}
                          className="px-3 py-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-100 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
                        >
                          Blank Template
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">
                        Upload Excel File (.xlsx, .xls)
                      </label>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            parseExcelFile(file);
                          }
                        }}
                        className="input-field"
                        disabled={bulkLoading}
                      />
                      <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 text-xs space-y-1 text-gray-600 dark:text-gray-300">
                        <p><strong>💡 How to bulk update missing phone numbers:</strong></p>
                        <ol className="list-decimal pl-4 space-y-0.5">
                          <li>Click <strong>"Export All Businesses"</strong> to download the current Excel sheet.</li>
                          <li>Open it in Excel, find rows where Phone is "N/A", and type the real contact number.</li>
                          <li>Save and upload the file above. It will match rows by <strong>ID</strong> and update them in bulk!</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  {/* Pending Updates Notice Banner */}
                  {pendingUpdates.length > 0 && (
                    <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/30 border-2 border-emerald-300 dark:border-emerald-600 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-scale-in">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></span>
                          <h4 className="font-bold text-emerald-900 dark:text-emerald-100 text-base">
                            {pendingUpdates.length} Businesses Ready for Bulk Update
                          </h4>
                        </div>
                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                          File matched by Business ID. Click <strong>"Apply Updates"</strong> to save new contact numbers and info to the database.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleApplyBulkUpdates}
                        disabled={bulkLoading}
                        className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-sm shadow-md hover:shadow-lg transition-all whitespace-nowrap active:scale-95 disabled:opacity-50"
                      >
                        {bulkLoading ? 'Updating Database...' : `Apply Updates (${pendingUpdates.length})`}
                      </button>
                    </div>
                  )}

                  {/* Manual Entry Table */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-50">Businesses to Add</h3>
                      <button
                        onClick={() => {
                          setBulkBusinesses([...bulkBusinesses, { name: '', category: '', subcategory: '', city: '', address: '', phone: '', email: '', website: '', description: '', tags: [], status: 'approved', featured: false }]);
                        }}
                        className="btn-outline text-sm py-1.5 px-3"
                      >
                        + Add Row
                      </button>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Name *</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Category *</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">City *</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Phone *</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {bulkBusinesses.map((business, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  value={business.name}
                                  onChange={(e) => {
                                    const updated = [...bulkBusinesses];
                                    updated[index].name = e.target.value;
                                    setBulkBusinesses(updated);
                                  }}
                                  placeholder="Business Name"
                                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <div className="flex gap-1">
                                  <select
                                    value={business.category}
                                    onChange={(e) => {
                                      const updated = [...bulkBusinesses];
                                      updated[index].category = e.target.value;
                                      updated[index].subcategory = ''; // Reset subcategory
                                      setBulkBusinesses(updated);
                                    }}
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                                  >
                                    <option value="">Select</option>
                                    {categories.map((cat) => (
                                      <option key={cat._id} value={cat._id}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => setShowCreateCategoryModal(true)}
                                    className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded font-semibold transition-colors"
                                    title="Create New Category"
                                  >
                                    +
                                  </button>
                                </div>
                              </td>
                              <td className="px-2 py-2">
                                <select
                                  value={business.city}
                                  onChange={(e) => {
                                    const updated = [...bulkBusinesses];
                                    updated[index].city = e.target.value;
                                    setBulkBusinesses(updated);
                                  }}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                                >
                                  <option value="">Select</option>
                                  {cities.map((city) => (
                                    <option key={city._id} value={city._id}>
                                      {city.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="tel"
                                  value={business.phone}
                                  onChange={(e) => {
                                    const updated = [...bulkBusinesses];
                                    updated[index].phone = e.target.value;
                                    setBulkBusinesses(updated);
                                  }}
                                  placeholder="Phone"
                                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <select
                                  value={business.status}
                                  onChange={(e) => {
                                    const updated = [...bulkBusinesses];
                                    updated[index].status = e.target.value;
                                    setBulkBusinesses(updated);
                                  }}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                                >
                                  <option value="approved">Approved</option>
                                  <option value="pending">Pending</option>
                                  <option value="rejected">Rejected</option>
                                </select>
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  onClick={() => {
                                    setBulkBusinesses(bulkBusinesses.filter((_, i) => i !== index));
                                  }}
                                  className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-xs"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Expandable Details for Each Business */}
                    {bulkBusinesses.map((business, index) => (
                      <div key={index} className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <details className="text-sm">
                          <summary className="cursor-pointer text-gray-700 dark:text-gray-300 font-medium mb-2">
                            {business.name || `Business ${index + 1}`} - Additional Details
                          </summary>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Address</label>
                              <textarea
                                value={business.address || ''}
                                onChange={(e) => {
                                  const updated = [...bulkBusinesses];
                                  updated[index].address = e.target.value;
                                  setBulkBusinesses(updated);
                                }}
                                rows="2"
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Subcategory</label>
                              <select
                                value={business.subcategory || ''}
                                onChange={(e) => {
                                  const updated = [...bulkBusinesses];
                                  updated[index].subcategory = e.target.value;
                                  setBulkBusinesses(updated);
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                              >
                                <option value="">Select</option>
                                {business.category && subcategories.filter(s => s.categoryId === business.category || s.categoryId?._id === business.category).map((subcat) => (
                                  <option key={subcat._id} value={subcat._id}>
                                    {subcat.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Email</label>
                              <input
                                type="email"
                                value={business.email || ''}
                                onChange={(e) => {
                                  const updated = [...bulkBusinesses];
                                  updated[index].email = e.target.value;
                                  setBulkBusinesses(updated);
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Website</label>
                              <input
                                type="url"
                                value={business.website || ''}
                                onChange={(e) => {
                                  const updated = [...bulkBusinesses];
                                  updated[index].website = e.target.value;
                                  setBulkBusinesses(updated);
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Description</label>
                              <textarea
                                value={business.description || ''}
                                onChange={(e) => {
                                  const updated = [...bulkBusinesses];
                                  updated[index].description = e.target.value;
                                  setBulkBusinesses(updated);
                                }}
                                rows="2"
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs text-gray-600 dark:text-gray-400">Tags</label>
                                <button
                                  type="button"
                                  onClick={() => setShowCreateTagModal(true)}
                                  className="px-2 py-0.5 text-xs bg-primary-600 hover:bg-primary-700 text-white rounded font-semibold transition-colors"
                                  title="Create New Tag"
                                >
                                  + New Tag
                                </button>
                              </div>
                              {/* Selected tags pills */}
                              {(business.tags || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {(business.tags || []).map(tagId => {
                                    const t = tags.find(tg => tg._id === tagId);
                                    if (!t) return null;
                                    return (
                                      <span key={tagId} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-primary-600 text-white">
                                        {t.name}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updated = [...bulkBusinesses];
                                            updated[index].tags = (updated[index].tags || []).filter(id => id !== tagId);
                                            setBulkBusinesses(updated);
                                          }}
                                          className="hover:text-primary-200"
                                        >×</button>
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                              {/* Search input */}
                              <div className="relative mb-1">
                                <input
                                  type="text"
                                  placeholder="Search tags..."
                                  className="w-full pl-6 pr-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                  onChange={(e) => {
                                    const updated = [...bulkBusinesses];
                                    updated[index]._tagSearch = e.target.value;
                                    setBulkBusinesses(updated);
                                  }}
                                  value={business._tagSearch || ''}
                                />
                                <svg className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                              </div>
                              {/* Filtered tag list */}
                              <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
                                {tags
                                  .filter(tag => {
                                    if ((business.tags || []).includes(tag._id)) return false;
                                    const q = (business._tagSearch || '').toLowerCase();
                                    return !q || tag.name.toLowerCase().includes(q);
                                  })
                                  .map((tag) => (
                                    <button
                                      key={tag._id}
                                      type="button"
                                      onClick={() => {
                                        const updated = [...bulkBusinesses];
                                        if (!updated[index].tags) updated[index].tags = [];
                                        updated[index].tags = [...updated[index].tags, tag._id];
                                        setBulkBusinesses(updated);
                                      }}
                                      className="px-2 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-700 dark:hover:text-primary-300 transition border border-gray-200 dark:border-gray-600"
                                    >
                                      + {tag.name}
                                    </button>
                                  ))}
                              </div>
                            </div>
                            <div>
                              <label className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                                <input
                                  type="checkbox"
                                  checked={business.featured || false}
                                  onChange={(e) => {
                                    const updated = [...bulkBusinesses];
                                    updated[index].featured = e.target.checked;
                                    setBulkBusinesses(updated);
                                  }}
                                  className="mr-1"
                                />
                                Featured
                              </label>
                            </div>
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>

                  {/* Progress Indicator */}
                  {bulkProgress.total > 0 && (
                    <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          Progress: {bulkProgress.completed + bulkProgress.failed} / {bulkProgress.total}
                        </span>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Success: {bulkProgress.completed} | Failed: {bulkProgress.failed}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full transition-all"
                          style={{ width: `${((bulkProgress.completed + bulkProgress.failed) / bulkProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* Results */}
                  {bulkProgress.results.length > 0 && (
                    <div className="mb-4 max-h-40 overflow-y-auto">
                      {bulkProgress.results.map((result, idx) => (
                        <div
                          key={idx}
                          className={`p-2 mb-2 rounded text-xs ${result.success
                            ? 'bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-300'
                            : 'bg-error-50 dark:bg-error-900/20 text-error-700 dark:text-error-300'
                            }`}
                        >
                          {result.success ? '✓' : '✗'} {result.name}: {result.message}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    {bulkMode === 'update' && pendingUpdates.length > 0 ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleApplyBulkUpdates();
                        }}
                        disabled={bulkLoading}
                        className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl flex-1 shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bulkLoading ? 'Applying Updates to Database...' : `Apply Updates to ${pendingUpdates.length} Businesses`}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleBulkSubmit();
                        }}
                        disabled={bulkLoading || bulkBusinesses.length === 0}
                        className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {bulkLoading ? `Adding... (${bulkProgress.completed + bulkProgress.failed}/${bulkProgress.total})` : `Add ${bulkBusinesses.length} Business${bulkBusinesses.length !== 1 ? 'es' : ''}`}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setShowBulkModal(false);
                        setBulkBusinesses([]);
                        setPendingUpdates([]);
                        setBulkMode('create');
                        setBulkProgress({ total: 0, completed: 0, failed: 0, results: [] });
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminBusinesses = () => {
  return (
    <AdminLayout>
      <AdminBusinessesContent />
    </AdminLayout>
  );
};

export default AdminBusinesses;

